'use client'
import Image from 'next/image'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { loginSchema, type LoginFormData } from '@/lib/validations'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { Eye, EyeOff, Loader2, Shield, Lock, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'

export default function LoginPage() {
  const router = useRouter()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      const json = await res.json()
      if (!res.ok) {
        toast.error(json.error || 'Login failed')
        return
      }
      toast.success(`Welcome back, ${json.admin.username}!`)
      router.push('/admin/dashboard')
      router.refresh()
    } catch {
      toast.error('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex" style={{ background: '#1a0d04' }}>
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 gov-header-gradient relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-15">
          <div className="absolute top-20 right-20 w-64 h-64 rounded-full" style={{ background: '#F39C12' }} />
          <div className="absolute bottom-20 left-10 w-48 h-48 rounded-full" style={{ background: '#E67E22' }} />
          <div className="absolute top-1/2 left-1/2 w-96 h-96 rounded-full -translate-x-1/2 -translate-y-1/2" style={{ background: '#4A2E14', opacity: 0.5 }} />
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center shadow-2xl border-2 border-white/20 overflow-hidden bg-white">
              <Image src="/logo.jpeg" alt="Logo" width={56} height={56} className="object-cover w-full h-full" />
            </div>
            <div>
              <div className="text-white font-bold text-2xl" style={{ fontFamily: 'Poppins, sans-serif' }}>Pune Pratham</div>
              <div className="text-white/60 text-sm">Administration Portal</div>
            </div>
          </div>
        </div>
        
        <div className="relative z-10 flex-1 flex flex-col justify-center">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Citizen Management
            <span className="block" style={{ color: '#F39C12' }}>Made Simple</span>
          </h2>
          <p className="text-white/70 text-lg mb-8">
            Manage citizen registrations, analytics, and communications from one powerful dashboard.
          </p>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Registrations', value: '50,000+' },
              { label: 'Districts Covered', value: '250+' },
              { label: 'States', value: '32' },
              { label: 'Uptime', value: '99.9%' },
            ].map(item => (
              <div key={item.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-2xl font-bold text-white">{item.value}</div>
                <div className="text-white/60 text-sm">{item.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <div className="gov-gold-bar h-0.5 rounded-full mb-4" />
          <p className="text-white/50 text-sm">© {new Date().getFullYear()} Pune Pratham. Secure Government Portal.</p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden bg-white shadow-md">
              <Image src="/logo.jpeg" alt="Logo" width={48} height={48} className="object-cover w-full h-full" />
            </div>
            <div>
              <div className="font-bold text-xl" style={{ color: '#E67E22' }}>Pune Pratham</div>
              <div className="text-gray-500 text-xs">Administration Portal</div>
            </div>
          </div>

          <Card className="border-0 shadow-2xl overflow-hidden">
            <div className="p-1 gov-gold-bar" />
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg" style={{ background: 'linear-gradient(135deg, #F39C12, #E67E22)' }}>
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold mb-1" style={{ color: '#E67E22', fontFamily: 'Poppins, sans-serif' }}>Admin Login</h1>
                <p className="text-gray-500 text-sm">Sign in to access the admin panel</p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div>
                  <Label htmlFor="username" className="text-sm font-medium text-gray-700">Username</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="username"
                      {...register('username')}
                      className="pl-10 border-gray-200 focus:border-[#E67E22] focus:ring-[#E67E22]/20"
                      placeholder="Enter username"
                      autoComplete="username"
                    />
                  </div>
                  {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username.message}</p>}
                </div>

                <div>
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      {...register('password')}
                      className="pl-10 pr-10 border-gray-200 focus:border-[#E67E22] focus:ring-[#E67E22]/20"
                      placeholder="Enter password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] disabled:opacity-70"
                  style={{ background: 'linear-gradient(135deg, #F39C12, #E67E22)', color: 'white' }}
                >
                  {isLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in...</>
                  ) : (
                    'Sign In to Dashboard'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <a href="/" className="text-sm text-gray-400 hover:text-[#F39C12] transition-colors">
                  ← Back to Registration Portal
                </a>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-gray-400 mt-4">
            Secure government portal. Unauthorized access is prohibited.
          </p>
        </div>
      </div>
    </div>
  )
}
